// Live-Verbindung (Server-Sent Events).
//
// Kein Token mehr in der URL: EventSource schickt bei gleicher Herkunft das
// Sitzungs-Cookie automatisch mit. Reconnect mit wachsendem Abstand, damit
// ein längerer Ausfall nicht im Sekundentakt gegen den Server läuft.
import type { ProductView, ShoppingListResponse } from '@speis/shared';
import { api } from './api.js';
import { CACHE_KEYS, cacheSet } from './idb.js';
import { flush } from './outbox.js';
import { applyProducts, applyShopping, setState, type ExpiringItem } from './store.js';

type Handler = (data: unknown) => void;

const extraHandlers = new Map<string, Set<Handler>>();

/** Zusätzlich auf ein Ereignis hören (z.B. „plan" im Wochenplan-Screen). */
export function onLiveEvent(event: string, handler: Handler): () => void {
  const set = extraHandlers.get(event) ?? new Set();
  set.add(handler);
  extraHandlers.set(event, set);
  return () => set.delete(handler);
}

function emit(event: string, data: unknown): void {
  for (const fn of extraHandlers.get(event) ?? []) fn(data);
}

let source: EventSource | null = null;
let attempt = 0;
let reconnectTimer: number | null = null;
let stopped = false;

export function connectLive(): void {
  stopped = false;
  open();
  window.addEventListener('online', () => {
    setState({ online: true });
    reconnectNow();
  });
  window.addEventListener('offline', () => setState({ online: false }));
  document.addEventListener('visibilitychange', () => {
    // Der Browser friert Verbindungen im Hintergrund ein; beim Zurückkommen
    // lieber neu aufbauen als auf ein totes Rohr zu warten.
    if (document.visibilityState === 'visible' && source?.readyState !== EventSource.OPEN) {
      reconnectNow();
    }
  });
}

export function disconnectLive(): void {
  stopped = true;
  source?.close();
  source = null;
  if (reconnectTimer !== null) window.clearTimeout(reconnectTimer);
  reconnectTimer = null;
}

function reconnectNow(): void {
  if (reconnectTimer !== null) window.clearTimeout(reconnectTimer);
  reconnectTimer = null;
  attempt = 0;
  source?.close();
  open();
}

function open(): void {
  if (stopped) return;
  source = new EventSource('/api/events');

  source.addEventListener('open', () => {
    attempt = 0;
    setState({ online: true });
    // Was offline aufgelaufen ist, geht jetzt raus.
    void flush();
  });

  source.addEventListener('shopping', (e) => {
    const data = parse<ShoppingListResponse>(e);
    if (data) applyShopping(data);
  });

  source.addEventListener('inventory', (e) => {
    const data = parse<{ products: ProductView[] }>(e);
    if (data?.products) applyProducts(data.products);
  });

  source.addEventListener('expiry', (e) => {
    emit('expiry', parse(e));
    void refreshExpiring();
  });

  for (const name of ['plan', 'recipes', 'food']) {
    source.addEventListener(name, (e) => emit(name, parse(e)));
  }

  source.addEventListener('error', () => {
    setState({ online: false });
    source?.close();
    source = null;
    if (stopped) return;
    // 3 s, 6 s, 12 s … maximal 60 s.
    const delay = Math.min(3000 * 2 ** attempt++, 60_000);
    reconnectTimer = window.setTimeout(open, delay);
  });
}

function parse<T>(event: Event): T | null {
  try {
    return JSON.parse((event as MessageEvent<string>).data || '{}') as T;
  } catch {
    return null;
  }
}

/** Ablaufliste nachladen (nach Buchungen und beim Start). */
export async function refreshExpiring(): Promise<void> {
  try {
    const { items } = await api<{ items: ExpiringItem[] }>('/api/expiring?days=7');
    setState({ expiring: items });
    void cacheSet(CACHE_KEYS.expiring, items);
  } catch {
    /* offline – der zwischengespeicherte Stand bleibt stehen */
  }
}
